import type {
  ProjectCleanupOptionId,
  ProjectCleanupPreview,
  ProjectCleanupResult,
  ProjectDetailResponse,
  ProjectEmbedding,
  ProjectEmbeddingUpdateResponse,
  ProjectResponse,
} from '../types/api'
import { tokenKey } from '../utils/org-slug'
import { apiClient } from './client'

export type CreateProjectEmbeddingInput = {
  provider: ProjectEmbedding['provider']
  model?: string
  dimensions?: number
  apiBaseUrl?: string | null
}

export async function getProjects(): Promise<ProjectResponse[]> {
  return apiClient.get<ProjectResponse[]>('/projects')
}

export async function getProject(id: number): Promise<ProjectDetailResponse> {
  return apiClient.get<ProjectDetailResponse>(`/projects/${id}`)
}

export async function createProject(
  name: string,
  description: string | undefined,
  embedding: CreateProjectEmbeddingInput | { embeddingProviderId: number },
  llmProviderId?: number
): Promise<ProjectResponse> {
  const embeddingPayload =
    'embeddingProviderId' in embedding
      ? { embeddingProviderId: embedding.embeddingProviderId }
      : { embedding }
  return apiClient.post<ProjectResponse>('/projects', {
    name,
    description,
    llmProviderId,
    ...embeddingPayload,
  })
}

export async function deleteProject(id: number): Promise<{ deleted: boolean }> {
  return apiClient.delete<{ deleted: boolean }>(`/projects/${id}`)
}

export async function previewProjectCleanup(projectId: number): Promise<ProjectCleanupPreview> {
  return apiClient.get<ProjectCleanupPreview>(`/projects/${projectId}/cleanup`)
}

export async function runProjectCleanup(
  projectId: number,
  options: ProjectCleanupOptionId[]
): Promise<ProjectCleanupResult> {
  return apiClient.post<ProjectCleanupResult>(`/projects/${projectId}/cleanup`, { options })
}

export async function updateProject(
  id: number,
  data: {
    description?: string | null
    contextualEnrichmentEnabled?: boolean
    transcriptDistillationEnabled?: boolean
  }
): Promise<
  Pick<
    ProjectResponse,
    | 'id'
    | 'name'
    | 'description'
    | 'contextualEnrichmentEnabled'
    | 'transcriptDistillationEnabled'
    | 'createdAt'
    | 'updatedAt'
  >
> {
  return apiClient.patch(`/projects/${id}`, data)
}

/**
 * Relink a project to a different system embedding_providers entry. The server rejects the
 * change (409) unless the target has identical dimensions/pipelineVersion to what's already
 * indexed — the embedding model itself can never change, only which provider serves it.
 */
export async function relinkProjectEmbeddingProvider(
  id: number,
  embeddingProviderId: number
): Promise<ProjectEmbeddingUpdateResponse> {
  return apiClient.patch<ProjectEmbeddingUpdateResponse>(`/projects/${id}/embedding-provider`, {
    embeddingProviderId,
  })
}

export async function uploadProjectIcon(id: number, file: File): Promise<{ iconUrl: string }> {
  return apiClient.upload<{ iconUrl: string }>(`/projects/${id}/icon`, file)
}

export async function deleteProjectIcon(id: number): Promise<{ deleted: boolean }> {
  return apiClient.delete<{ deleted: boolean }>(`/projects/${id}/icon`)
}

export function getAuthenticatedIconUrl(iconUrl: string | null | undefined): string | null {
  if (!iconUrl) return null
  // Org-prefixed token first: bare 'accessToken' holds another org's session
  // when two orgs are open in different tabs (see tokenKey()).
  const token =
    localStorage.getItem(tokenKey('accessToken')) ??
    localStorage.getItem('accessToken') ??
    localStorage.getItem(tokenKey('apiKey')) ??
    localStorage.getItem('apiKey')
  if (!token) return iconUrl
  const separator = iconUrl.includes('?') ? '&' : '?'
  return `${iconUrl}${separator}token=${encodeURIComponent(token)}`
}
